<template>
  <chart-container
    :titleChart="titleChart"
    @onCSVDownload="downloadCsv(titleChart)"
    @zoomEnabled="zoomGraph(true)"
    @zoomDisabled="zoomGraph(false)"
    :downloadId="downloadId"
    :specificSiteId="specificSiteId"
    :zoomGraphValue="zoomGraphValue"
    :downloadShow="dataSource && dataSource.dataset.length > 0 && dataSource.dataset[0] && dataSource.dataset[0].data && Object.keys(dataSource.dataset[0].data).length > 0"
    class="bg-gradient-blue"
    :class="{ 'no-data-blue': dataSource && dataSource.dataset.length == 0 }"
    titleTooltip="This chart is intended to show the relationship between participant retention rates and their respective non-compliance rates. Each bubble in the chart below represents a participant who discontinued or is no longer participating in the study. Hover over each participant to get more information, or change to the Countries or Sites tab to see the differences between the groups."
    :showThreadLogoInUnexpand="true"
  >
    <span slot="header" class="float-right enrol-tabs">
      <b-tabs>
        <b-tab v-for="(tabName, index) in tabs" :key="index" :title="tabName" @click="tabChange(tabName)" />
      </b-tabs>
    </span>
    <fusioncharts style="width: 100%" :type="type" :width="width" :height="height" :dataFormat="dataFormat" :dataSource="dataSource" dataEmptyMessage="No Data In" ref="nonComplianceRateVSRetentionRate" v-if="dataSource && dataSource.dataset.length > 0 && dataSource.dataset[0] && dataSource.dataset[0].data && Object.keys(dataSource.dataset[0].data).length > 0" slot="body" />

    <div v-else class="align-center p-60" slot="body">
      <div class="card-text-title no-data" v-if="dataLoaded">No Data In</div>
    </div>
  </chart-container>
</template>

<script>
import ChartContainer from "./chart-container-dark-blue.vue";
import _cloneDeep from "lodash/cloneDeep";
import * as sExecutive from "@/services/visualization/study-executive";
import * as dManagement from "@/services/visualization/data-management";
import { getColorPalette } from "@/services/visualization/utils";

export default {
  components: {
    ChartContainer
  },
  props: {
    titleChart: { type: String, default: "" },
    downloadId: { type: String, default: "" },
    zoomGraphValue: { type: String, default: "" },
    specificSiteId: [Object, Array],
    countrySelected: [Array, String],
    sitesSelected: [Array, String],
    rawData: { type: Object, default: () => {} },
    dataLoaded: { type: Boolean, default: false }
  },

  data() {
    return {
      zoomTrigger: false,
      type: "scatter",
      width: "100%",
      height: "250",
      dataFormat: "json",
      dataSource: {
        chart: {
          xAxisName: "Non-Compliance Rate",
          yAxisName: "Participant Retention Rate",
          ynumbersuffix: "%",
          xAxisMinValue: "0",
          xAxisMaxValue: "100",
          xNumberSuffix: "%",
          yAxisMinValue: "0",
          yAxisMaxValue: "100",
          theme: "fusion",
          plothighlighteffect: "fadeout|alpha=15, regressionlinealpha=15",
          plottooltext: "$seriesname{br}Non-Compliance: $xvalue%{br}Retention: $yvalue%",
          regressionlinealpha: "50",
          anchorsides: "1",
          divlinealpha: "0",
          paletteColors: getColorPalette("dark"),
          xAxisNameFontColor: "#959fa4",
          xAxisNameFont: "RobotoRegular",
          xAxisNameFontSize: "10px",
          legenditemFontSize: "12px",
          legenditemFont: "RobotoRegular",
          legenditemFontColor: "#a3a3a3",
          labelFontSize: "10px",
          labelFont: "RobotoRegular",
          labelFontColor: "#a3a3a3",
          yAxisNameFontColor: "#a3a3a3",
          yAxisNameFont: "RobotoRegular",
          yAxisNameFontSize: "10px",
          yAxisValueFont: "RobotoRegular",
          yAxisValueFontSize: "10px",
          yAxisValueFontColor: "#a3a3a3",
          useEllipsesWhenOverflow: "1",
          maxColWidth: "42",
          plotHighlightEffect: "fadeout",
          plotSpacePercent: 30,
          minimiseWrappingInLegend: true,
          showXAxisLine: true,
          showYAxisLine: true,
          xaxislinethickness: "1",
          yaxislinethickness: "1",
          xAxisLineColor: "#b0bac9",
          yAxisLineColor: "#b0bac9",
          chartBottomMargin: "0",
          xAxisNamePadding: "5",
          bgColor: "#00000000",
          bgAlpha: "0",
          vDivLineThickness: "0"
        },
        categories: [],
        dataset: []
      },
      tabs: ["Total", "Countries", "Sites"],
      activeTab: 0,
      activeTabName: "Total"
    };
  },
  watch: {
    zoomGraphValue: function(newVal) {
      this.zoomGraphValueChild = newVal;
      if (newVal == "") {
        this.height = "250";
      }
    },
    rawData: function(newVal) {
      const response = sExecutive.getNonComplianceRateVSRetentionRate(newVal, this.countrySelected, this.sitesSelected, this.activeTabName);
      this.setGraphDataAndConfigs(response);
    },
    countrySelected: function(newVal) {
      const response = sExecutive.getNonComplianceRateVSRetentionRate(this.rawData, newVal, this.sitesSelected, this.activeTabName);
      this.setGraphDataAndConfigs(response);
    },
    sitesSelected: function(newVal) {
      const response = sExecutive.getNonComplianceRateVSRetentionRate(this.rawData, this.countrySelected, newVal, this.activeTabName);
      this.setGraphDataAndConfigs(response);
    },
    activeTabName: function(newVal) {
      const response = sExecutive.getNonComplianceRateVSRetentionRate(this.rawData, this.countrySelected, this.sitesSelected, newVal);
      this.setGraphDataAndConfigs(response);
    }
  },
  methods: {
    tabChange(newValue) {
      this.activeTab = newValue;
      if (newValue === "Total") {
        this.activeTabName = "Total";
        this.dataSource.chart.plottooltext = "Non-Compliance: $xvalue%{br}Retention: $yvalue%";
      } else if (newValue === "Countries") {
        this.activeTabName = "Countries";
        this.dataSource.chart.plottooltext = "$seriesname{br}Non-Compliance: $xvalue%{br}Retention: $yvalue%";
      } else if (newValue === "Sites") {
        this.activeTabName = "Sites";
        this.dataSource.chart.plottooltext = "$seriesname{br}Non-Compliance: $xvalue%{br}Retention: $yvalue%";
      }
    },
    setGraphDataAndConfigs(response = null) {
      if (response) {
        const dataSources = _cloneDeep(this.dataSource);
        dataSources.dataset = response;
        this.dataSource = dataSources;
      }
    },
    downloadCsv(titleChart) {
      if (this.activeTabName === "Total") {
        const header = ["Participant", "Non-Compliance %", "Retention Rate %", "Site", "Country"];
        const newArr = [];
        const response = sExecutive.getNonComplianceRateVSRetentionRate(this.rawData, this.countrySelected, this.sitesSelected, this.activeTabName);
        response.csvData.forEach(data => {
          newArr.push([data.Participant, data.NonComplianceRate, data.RetentionRate, data.Sites, data.country]);
        });
        dManagement.downloadCSV(titleChart, header, newArr, this.specificSiteId);
      } else if (this.activeTabName === "Countries") {
        const header = ["Country", "Non-Compliance %", "Retention Rate %"];
        const newArr = [];
        const response = sExecutive.getNonComplianceRateVSRetentionRate(this.rawData, this.countrySelected, this.sitesSelected, this.activeTabName);
        response.csvData.forEach(data => {
          newArr.push([data.country, data.NonComplianceRate, data.RetentionRate]);
        });
        dManagement.downloadCSV(titleChart, header, newArr, this.specificSiteId);
      } else if (this.activeTabName === "Sites") {
        const header = ["Site", "Non-Compliance %", "Retention Rate %"];
        const newArr = [];
        const response = sExecutive.getNonComplianceRateVSRetentionRate(this.rawData, this.countrySelected, this.sitesSelected, this.activeTabName);
        response.csvData.forEach(data => {
          newArr.push([data.Sites, data.NonComplianceRate, data.RetentionRate]);
        });
        dManagement.downloadCSV(titleChart, header, newArr, this.specificSiteId);
      }
    },
    zoomGraph(status) {
      if (status) {
        this.zoomGraphValueChild = true;
        this.height = "400";
        this.$emit("zoomEnabled");
      } else {
        this.zoomGraphValueChild = false;
        this.height = "250";
        this.$emit("zoomDisabled");
      }
    }
  }
};
</script>

<style lang="scss">
.bg-compliance-insights {
  background-color: #243e64;

  &.no-data-blue {
    background-color: #35537e;

    .bg-dark-blue {
      background-color: #35537e;
    }
  }

  .activity-compliance-score {
    width: 100%;
    max-width: 261px;
    height: 130px;
    background-color: #35537e;
    border-radius: 4px;
    margin-top: 0;
  }

  .btn-round {
    width: 13.3px;
    height: 13.3px;
    opacity: 0.7;
    background-color: #d9d9d9;
  }

  .help-icon {
    color: #d9d9d9;
  }

  .help-icon-hover {
    div {
      font-size: 10px !important;
    }
  }

  .tooltip {
    padding: 8px;
    border-radius: 4px;
    font-family: "Roboto-Medium", sans-serif;
    font-size: 12px;
    color: #ffffff;
    background-color: #c3d5ee;
  }

  .chevron-up {
    width: 0px;
    height: 0px;
    border-left: 19px solid transparent;
    border-right: 19px solid transparent;
    border-bottom: 17px solid #48bf7a;
    margin-left: 5px;
  }

  .chevron-down {
    width: 0;
    height: 0;
    border-left: 19px solid transparent;
    border-right: 19px solid transparent;
    border-top: 17px solid #ff4040;
  }

  .rectangle {
    width: 25px;
    height: 4px;
    margin: 1px 0 0;
    background-color: #959fa4;
  }

  .text-red {
    color: #ff4040;
  }

  .text-green {
    color: #48bf7a;
  }

  .text-grey {
    color: #959fa4;
  }

  .compliance-text {
    font-weight: 300;
    font-family: "Roboto-Light";
    font-size: 16px;
    height: 157px;
    background-color: #10284b;
    padding: 5px;
    cursor: default;
    color: white;
    width: 273px;

    overflow-y: scroll;
    scrollbar-color: #b0bac9 transparent; /* thumb and track color */
    scrollbar-width: 7px;

    &::-webkit-scrollbar {
      width: 7px;
      background-color: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #b0bac9;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-track {
      background-color: transparent;
    }
  }

  .align-content-textarea {
    width: 100%;
    height: 157px;
    display: flex;
    background-color: #10284b;
    justify-content: center;
  }

  .activity-compliance-label {
    font-size: 14px;
    color: white;
    font-family: Roboto-Medium;
    font-weight: 500;
  }
  .parent-overlay {
    position: relative;

    .gradient-overlay {
      display: block;
      position: absolute;
      height: calc(156px / 2);
      width: 100%;
      bottom: 0;
      left: 0;
      background: rgb(36, 62, 100);
      background: linear-gradient(180deg, rgba(36, 62, 100, 0) 0%, rgba(36, 62, 100, 1) 100%);
      z-index: 100;
      pointer-events: none;
    }
  }
}
</style>
